import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import {
  useDispatch,
  useSelector,
  usePublicGroup,
  useTabsState,
  useRequiredParams,
} from '_common/hooks';
import { navigateToMyFiles } from 'router/navigation';

import { setCurrentAppPage } from 'App/redux/appSlice';

import { ErrorView, IntlErrorBoundary, DocumentTitle } from '_common/components';
import { Tabs } from '@ets/design-system';

import GeneralView from './GeneralView';
import MembersView from './MembersView/MembersView';
import AdminsView from './AdminsView/AdminsView';

import styles from './GroupSettingsPage.module.scss';
import Header from './Header';

const GroupSettingsPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { id: groupId } = useRequiredParams<{ id: ObjectId }>();
  const { group: data } = usePublicGroup(groupId);

  const error = useSelector((state) => state.app.error);
  const { tab, onTabChange } = useTabsState('groupSettings');

  useEffect(() => {
    dispatch(setCurrentAppPage('/settings/group'));
  }, []);

  const checkErrorStatus = () => {
    return error.status === 400 || error.status === 403 || error.status === 404;
  };

  // check for forbidden view
  if (checkErrorStatus()) {
    return <ErrorView error={error} onClick={navigateToMyFiles} />;
  }

  if (!data) {
    return <div />;
  }

  return (
    <DocumentTitle title={`doDOC - ${data.name}`}>
      <div className={styles.root}>
        <div className={styles.header}>
          <IntlErrorBoundary size="s" style={{ margin: '0 0 0 7rem' }}>
            <Header />
          </IntlErrorBoundary>
        </div>
        <div className={styles.page}>
          <IntlErrorBoundary size="xl" style={{ margin: '13.5rem 0 0 0' }}>
            <Tabs value={tab || 'general'} onValueChange={onTabChange} className={styles.content}>
              <Tabs.List>
                <Tabs.Trigger value="general">
                  {intl.formatMessage({ id: 'settings.tabs.general' })}
                </Tabs.Trigger>
                <Tabs.Trigger value="admins">
                  {intl.formatMessage({ id: 'settings.tabs.admins' })}
                </Tabs.Trigger>
                <Tabs.Trigger value="members">
                  {intl.formatMessage({ id: 'groups.header.member' }, { membersCount: 0 })}
                </Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="general">
                <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
                  <GeneralView />
                </IntlErrorBoundary>
              </Tabs.Content>
              <Tabs.Content
                value="admins"
                className={tab === 'admins' ? styles.content : undefined}
              >
                <IntlErrorBoundary key="admins" size="xl" style={{ margin: '5rem 0' }}>
                  <AdminsView />
                </IntlErrorBoundary>
              </Tabs.Content>
              <Tabs.Content
                value="members"
                className={tab === 'members' ? styles.content : undefined}
              >
                <IntlErrorBoundary key="members" size="xl" style={{ margin: '5rem 0' }}>
                  <MembersView />
                </IntlErrorBoundary>
              </Tabs.Content>
            </Tabs>
          </IntlErrorBoundary>
        </div>
      </div>
    </DocumentTitle>
  );
};

export default GroupSettingsPage;
